import {
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns"
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz"

import { TTimezone } from "src/data/timezones/timezones"

import { formatDate } from "./l10n"

export const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Immutable date utility that stores time as UTC milliseconds and applies
 * timezone-aware transformations using IANA timezone identifiers.
 *
 * Instances are safe to pass around and will not mutate. Use helper methods to
 * derive new instances at day/week/month/year boundaries in the specified
 * timezone.
 *
 * Reason for this class:
 * - This class never transforms the date into something that is not UTC
 * - Instead it has helper methods that apply timezone-aware transformations
 * - This differs a bit from using the date-fns-tz toZonedTime and fromZonedTime, as those will actually transform the date and
 *   return a monster of a date object that is not UTC
 */
export class ZonedDateTime {
  // IANA timezone identifier
  readonly timezone: string

  // UTC milliseconds
  readonly utcTime: number

  constructor(date: Date | number, timezone: string | TTimezone) {
    // Allow passing Date as either number or Date object
    if (typeof date === "number") {
      // If the date is a number, it is already assumed to be UTC milliseconds
      this.utcTime = date
    } else {
      // If the date is a Date object, convert it to UTC milliseconds
      this.utcTime = date.getTime()
    }

    // Allow passing timezone as either string or TTimezone object
    if (typeof timezone === "string") {
      // If the timezone is a string, it is already assumed to be an IANA timezone identifier
      this.timezone = timezone
    } else {
      // If the timezone is a TTimezone object, extract the IANA timezone identifier
      this.timezone = timezone.tzCode
    }
  }

  /**
   * Format the stored UTC time using l10n-aware `formatDate` in the configured
   * timezone. Only pass non-date options; date and timezone are injected.
   */
  format(
    args: Omit<Parameters<typeof formatDate>[0], "date" | "timezone"> = {}
  ) {
    return formatDate({
      ...args,
      date: new Date(this.utcTime).toISOString(),
      timezone: this.timezone,
    })
  }

  /**
   * Format using a raw format string applied in the instance's timezone.
   *
   * @param formatString date-fns format string
   */
  customFormat(formatString: string) {
    return formatInTimeZone(this.utcTime, this.timezone, formatString)
  }

  /**
   * Convert to a native Date based on the stored UTC milliseconds, taking into account the timezone.
   *
   * USE WITH CAUTION. This returns a date object that IS NOT IN UTC.
   * Consider the return value of this to be holder of year, month, day, hour, minute, second and so on and NOT AN ACTUAL "date"
   *
   * This is useful in components that work with dates, but do not consider timezones (DatePickerDropdown for example).
   *
   * @returns Date in the instance's timezone
   */
  asZonedDate() {
    return toZonedTime(this.utcTime, this.timezone)
  }

  /**
   * Return a new instance with the same UTC time but a different timezone.
   * This will not change the date, it will just change the timezone.
   * Example:
   * Given an instance "myDate" that has utc time "2025-03-05T09:00:00Z"
   * and timezone "Europe/Stockholm" (in other words "2025-03-05T10:00:00+01:00")
   *
   * Then calling myDate.updateTimezone("America/New_York")
   * would return an instance with the same utc time "2025-03-05T09:00:00Z"
   * but timezone "America/New_York" (in other words "2025-03-05T05:00:00-04:00")
   */
  withTimezone(timezone: string | TTimezone) {
    return new ZonedDateTime(this.utcTime, timezone)
  }

  /**
   * Apply a date-fns mutator in the instance's timezone and return a new
   * instance with the resulting UTC time preserved.
   *
   * The reason for this method is to be able to set start/end of day, week etc contextually of the correct timezone.
   * For example:
   * const utcTime = new ZonedDateTime("2025-03-05T09:00:00Z", "America/New_York").timezoneAwareModification((date) => startOfYear(date)).utcTime
   * then utcTime would be "2025-01-01T04:00:00Z" ("2025-01-01T00:00:00-04:00")
   */
  transformDate(mutator: (date: Date) => Date) {
    const copy = this.clone()
    const zonedDate = toZonedTime(copy.utcTime, copy.timezone)
    const zonedModifiedDate = mutator(zonedDate)
    return new ZonedDateTime(
      fromZonedTime(zonedModifiedDate, copy.timezone),
      copy.timezone
    )
  }

  /**
   * End-of-day inclusive upper bound in the instance's timezone.
   * See timezoneAwareModification for more details.
   */
  atEndOfDay() {
    return this.transformDate(endOfDay)
  }

  /**
   * Start-of-day in the instance's timezone.
   * See timezoneAwareModification for more details.
   */
  atStartOfDay() {
    return this.transformDate(startOfDay)
  }

  /**
   * Start-of-week in the instance's timezone.
   * See timezoneAwareModification for more details.
   *
   * @param options date-fns startOfWeek options (e.g., weekStartsOn)
   */
  atStartOfWeek(options?: Parameters<typeof startOfWeek>[1]) {
    return this.transformDate((date) => startOfWeek(date, options))
  }

  /**
   * Convert to a native Date based on the stored UTC milliseconds.
   */
  asUTCDate() {
    return new Date(this.utcTime)
  }

  /**
   * Start-of-month in the instance's timezone.
   * See timezoneAwareModification for more details.
   */
  atStartOfMonth() {
    return this.transformDate(startOfMonth)
  }

  /**
   * Start-of-year in the instance's timezone.
   * See timezoneAwareModification for more details.
   */
  atStartOfYear() {
    return this.transformDate(startOfYear)
  }

  /**
   * End-of-week in the instance's timezone.
   * See timezoneAwareModification for more details.
   *
   * @param options date-fns endOfWeek options (e.g., weekStartsOn)
   */
  atEndOfWeek(options?: Parameters<typeof endOfWeek>[1]) {
    return this.transformDate((date) => endOfWeek(date, options))
  }

  /**
   * End-of-month in the instance's timezone.
   * See timezoneAwareModification for more details.
   */
  atEndOfMonth() {
    return this.transformDate(endOfMonth)
  }

  /**
   * End-of-year in the instance's timezone.
   * See timezoneAwareModification for more details.
   */
  atEndOfYear() {
    return this.transformDate(endOfYear)
  }

  /**
   * Create a shallow copy of this instance.
   */
  clone() {
    return ZonedDateTime.clone(this)
  }

  /**
   * Clone a ZonedDateTime instance.
   */
  static clone(zonedDateTime: ZonedDateTime) {
    return new ZonedDateTime(zonedDateTime.utcTime, zonedDateTime.timezone)
  }

  /**
   * Comparable primitive value. This compares using the UTC time only and
   * does NOT consider the timezone.
   *
   * The reason is that regardless of timezone the UTC time is the same.
   *
   * For example:
   * new ZonedDateTime("2025-03-05T09:00:00Z", "Europe/London").valueOf()
   * new ZonedDateTime("2025-03-05T09:00:00Z", "America/New_York").valueOf()
   * WILL BE EQUAL
   */
  valueOf() {
    return this.utcTime
  }

  /**
   * Create an instance for "now" in the given timezone.
   *
   * @param timezone IANA timezone identifier or a TTimezone object
   * @param clockType 12 or 24 hour clock type
   */
  static now(timezone: string | TTimezone) {
    return new ZonedDateTime(new Date(), timezone)
  }

  /**
   * Create an instance from a date in the local timezone.
   *
   * @param date Date in the local timezone
   * @param timezone IANA timezone identifier or a TTimezone object
   *
   * @returns ZonedDateTime instance in the given timezone, with time converted to UTC
   */
  static fromDateInLocalTimezone(date: Date, timezone: string | TTimezone) {
    const timezoneString =
      typeof timezone === "string" ? timezone : timezone.tzCode
    return new ZonedDateTime(
      fromZonedTime(date, timezoneString).getTime(),
      timezoneString
    )
  }

  /**
   * Generate all days in the inclusive interval, respecting the timezone.
   * Throws if timezones or clock types differ.
   */
  static eachDayOfInterval({
    start,
    end,
  }: {
    start: ZonedDateTime
    end: ZonedDateTime
  }) {
    if (start.timezone !== end.timezone) {
      throw new Error("Start and end dates must be in the same timezone")
    }

    const startDate = ZonedDateTime.clone(start)
    const endDate = ZonedDateTime.clone(end)
    const timezone = startDate.timezone
    const zonedStartDate = toZonedTime(startDate.utcTime, startDate.timezone)
    const zonedEndDate = toZonedTime(endDate.utcTime, endDate.timezone)
    const zonedEachDayOfIntervalDate = eachDayOfInterval({
      start: zonedStartDate,
      end: zonedEndDate,
    })

    const mappedDates = zonedEachDayOfIntervalDate.map(
      (date) => new ZonedDateTime(fromZonedTime(date, timezone), timezone)
    )

    return mappedDates
  }

  /**
   * Generate month starts in the inclusive interval, respecting the timezone.
   * Throws if timezones or clock types differ.
   */
  static eachMonthOfInterval({
    start,
    end,
  }: {
    start: ZonedDateTime
    end: ZonedDateTime
  }) {
    if (start.timezone !== end.timezone) {
      throw new Error("Start and end dates must be in the same timezone")
    }

    const startDate = start.clone()
    const endDate = end.clone()
    const timezone = startDate.timezone
    const zonedStartDate = toZonedTime(startDate.utcTime, startDate.timezone)
    const zonedEndDate = toZonedTime(endDate.utcTime, endDate.timezone)
    const zonedEachMonthOfIntervalDate = eachMonthOfInterval({
      start: zonedStartDate,
      end: zonedEndDate,
    })
    return zonedEachMonthOfIntervalDate.map(
      (date) => new ZonedDateTime(fromZonedTime(date, timezone), timezone)
    )
  }
}
